// src/components/shopping/CartOverlay.tsx
import React from 'react';
import { X, Plus, Minus, Trash2, ShoppingBag, ArrowRight, Store, Factory, Tag, MessageCircle } from 'lucide-react';
import { CartItem } from '../../contexts/CartContext';
import { formatCurrency } from '../../utils/formatters';

interface CartOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  cart: CartItem[];
  updateQuantity: (cartId: string, delta: number) => void;
  remove: (cartId: string) => void;
  checkout: () => void;
  getSellerType?: (sellerId: string) => 'brand' | 'designer' | 'thrift' | 'unknown';
  onOpenChat?: (sellerId: string, sellerName: string) => void;
}

export const CartOverlay: React.FC<CartOverlayProps> = ({
  isOpen,
  onClose,
  cart,
  updateQuantity,
  remove,
  checkout,
  getSellerType,
  onOpenChat,
}) => {
  // 🟢 PRICING LOGIC
  const subtotal = cart.reduce((acc, item) => acc + (item.price * (item.quantity || 1)), 0);
  const platformFee = cart.length > 0 ? 2 : 0;
  const total = subtotal + platformFee;

  // 🟢 DETECT SELLER TYPE
  const sellerType = React.useMemo(() => {
    if (!getSellerType || cart.length === 0) return 'unknown';
    
    for (const item of cart) {
      const type = getSellerType(item.sellerId || item.businessId || '');
      if (type === 'thrift') return 'thrift';
    }
    for (const item of cart) {
      const type = getSellerType(item.sellerId || item.businessId || '');
      if (type === 'designer') return 'designer';
    }
    for (const item of cart) {
      const type = getSellerType(item.sellerId || item.businessId || '');
      if (type === 'brand') return 'brand';
    }
    return 'unknown';
  }, [cart, getSellerType]);

  // 🟢 GET UNIQUE SELLERS IN CART
  const uniqueSellers = React.useMemo(() => {
    const sellers = new Map<string, { id: string; name: string; type: string }>();
    cart.forEach(item => {
      const sellerId = item.sellerId || item.businessId || '';
      const sellerName = item.sellerName || item.brand || 'Unknown Seller';
      if (sellerId && !sellers.has(sellerId)) {
        sellers.set(sellerId, {
          id: sellerId,
          name: sellerName,
          type: getSellerType?.(sellerId) || 'unknown'
        });
      }
    });
    return Array.from(sellers.values());
  }, [cart, getSellerType]);

  // 🟢 SELLER CONFIG
  const sellerConfig = {
    brand: {
      label: 'Brand Store',
      icon: Store,
      color: 'text-blue-500',
      bg: 'bg-blue-500/10',
      border: 'border-blue-500/30',
    },
    designer: {
      label: 'Designer Studio',
      icon: Factory,
      color: 'text-orange-500',
      bg: 'bg-orange-500/10',
      border: 'border-orange-500/30',
    },
    thrift: {
      label: 'Thrift Vintage',
      icon: Tag,
      color: 'text-amber-500',
      bg: 'bg-amber-500/10',
      border: 'border-amber-500/30',
    },
    unknown: {
      label: 'Store',
      icon: ShoppingBag,
      color: 'text-white/40',
      bg: 'bg-white/5',
      border: 'border-white/10',
    }
  };

  const config = sellerConfig[sellerType] || sellerConfig.unknown;
  const SellerIcon = config.icon;

  // 🟢 HANDLE CHECKOUT
  const handleCheckout = () => {
    if (cart.length === 0) return;

    const event = new CustomEvent('open-checkout', {
      detail: {
        sellerType: sellerType,
        items: cart,
        sellerId: cart[0]?.sellerId || cart[0]?.businessId || '',
        sellerName: cart[0]?.sellerName || cart[0]?.brand || 'Store',
        total: total,
        subtotal: subtotal,
        platformFee: platformFee
      }
    });
    window.dispatchEvent(event);
    checkout();
  };

  // 🟢 HANDLE OPEN CHAT - ONLY FOR THRIFT
  const handleOpenChat = (sellerId: string, sellerName: string) => {
    if (onOpenChat) {
      onOpenChat(sellerId, sellerName);
    } else {
      window.dispatchEvent(new CustomEvent('open-messages', {
        detail: { userId: sellerId }
      }));
    }
    onClose();
  };

  if (!isOpen) return null;

  // 🟢 CHECK IF CART HAS ANY THRIFT ITEMS
  const hasThriftItems = cart.some(item => {
    const type = getSellerType?.(item.sellerId || item.businessId || '');
    return type === 'thrift';
  });

  return (
    <div className={`fixed inset-0 z-[250] flex justify-end bg-black transition-opacity duration-300 ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
      
      {/* =========================================================================
          📱 MOBILE LAYOUT (< 1024px) - Bottom Sheet
      ========================================================================= */}
      <div className="lg:hidden absolute inset-0 z-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      
      <div className={`lg:hidden relative w-full h-[90vh] mt-auto bg-black rounded-t-[2rem] border-t border-zinc-800 shadow-2xl flex flex-col transform transition-transform duration-300 ease-out ${isOpen ? 'translate-y-0' : 'translate-y-full'} text-white`}>
        
        {/* Mobile Drag Handle */}
        <div className="w-full flex justify-center pt-3 pb-1">
            <div className="w-12 h-1.5 bg-zinc-800 rounded-full"></div>
        </div>

        {/* HEADER */}
        <div className="px-6 py-4 flex justify-between items-center border-b border-zinc-800 bg-black sticky top-0 z-10">
          <div className="flex items-center gap-2">
            <SellerIcon size={18} className={config.color} />
            <h2 className="text-2xl font-black uppercase italic tracking-tighter">My Bag</h2>
            {sellerType !== 'unknown' && (
              <span className={`text-[8px] font-black uppercase px-2 py-0.5 rounded-full ${config.bg} ${config.color} border ${config.border}`}>
                {config.label}
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-2 text-zinc-500 hover:text-white transition-colors bg-zinc-900 rounded-full">
            <X size={20} />
          </button>
        </div>
        
        {/* BODY */}
        <div className="flex-1 overflow-y-auto p-6 scrollbar-hide bg-black">
          {cart.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-zinc-600 opacity-60">
              <ShoppingBag size={64} strokeWidth={1} className="mb-6" />
              <p className="font-black uppercase tracking-[0.2em] text-sm text-white mb-2">Bag is empty</p>
              <p className="text-xs font-mono tracking-widest text-center">Add some fresh items<br/>to get started</p>
            </div>
          ) : (
            <div className="space-y-4">
              {cart.map((item) => {
                const maxStock = item.stock !== undefined ? item.stock : 99;
                const itemSellerType = getSellerType?.(item.sellerId || item.businessId || '') || 'unknown';
                const itemQuantity = item.quantity || 1;

                return (
                  <div key={item.cartId} className="flex gap-4 p-3 rounded-2xl border border-zinc-800 bg-zinc-950 group hover:border-zinc-700 transition-colors">
                    
                    {/* Image */}
                    <div className="w-24 h-28 shrink-0 rounded-xl overflow-hidden bg-zinc-900 relative">
                        <img 
                          src={item.image} 
                          className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" 
                          alt={item.name} 
                        />
                        {itemSellerType !== 'unknown' && (
                          <div className={`absolute top-1 left-1 px-1.5 py-0.5 rounded text-[6px] font-black uppercase ${itemSellerType === 'thrift' ? 'bg-amber-500 text-black' : itemSellerType === 'designer' ? 'bg-orange-500 text-white' : 'bg-blue-500 text-white'}`}>
                            {itemSellerType === 'thrift' ? 'VTG' : itemSellerType === 'designer' ? 'DSG' : 'BRD'}
                          </div>
                        )}
                    </div>

                    {/* Details */}
                    <div className="flex-1 flex flex-col justify-between py-1">
                      <div>
                        <div className="flex justify-between items-start mb-1">
                          <h4 className="font-black text-sm uppercase tracking-tight pr-4 leading-tight">{item.name}</h4>
                          <button type="button" onClick={() => remove(item.cartId)} className="text-zinc-600 hover:text-red-500 transition-colors p-1">
                            <Trash2 size={16} />
                          </button>
                        </div>
                        <p className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest mb-1">
                          {item.brand}
                        </p>
                        <p className="text-xs font-bold text-zinc-400">
                          {item.selectedSize} <span className="mx-1">•</span> {item.selectedColor}
                        </p>
                      </div>

                      <div className="flex justify-between items-end mt-2">
                        <span className="font-bold text-sm tracking-wider">{formatCurrency(item.price * itemQuantity)}</span>
                        
                        {/* Quantity Selector */}
                        <div className="flex items-center gap-4 bg-black border border-zinc-800 rounded-lg px-2 py-1">
                          <button onClick={() => updateQuantity(item.cartId, -1)} disabled={itemQuantity <= 1} className="disabled:opacity-30 text-white hover:text-zinc-400 transition-colors">
                            <Minus size={14} />
                          </button>
                          <span className="text-xs font-bold font-mono w-4 text-center">{itemQuantity}</span>
                          
                          <button onClick={() => updateQuantity(item.cartId, 1)} disabled={itemQuantity >= maxStock} className="disabled:opacity-30 text-white hover:text-zinc-400 transition-colors">
                            <Plus size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
              
              {/* 🟢 SELLER LIST - Show all sellers in cart */}
              {uniqueSellers.length > 1 && (
                <div className="mt-4 p-4 rounded-2xl bg-zinc-900 border border-zinc-800">
                  <p className="text-[8px] text-white/30 font-black uppercase tracking-widest mb-2">Items from multiple sellers</p>
                  <div className="flex flex-wrap gap-2">
                    {uniqueSellers.map(seller => (
                      <button
                        key={seller.id}
                        onClick={() => {
                          // Only allow chat for thrift sellers
                          if (seller.type === 'thrift') {
                            handleOpenChat(seller.id, seller.name);
                          }
                        }}
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 border rounded-lg transition-colors ${
                          seller.type === 'thrift' 
                            ? 'bg-amber-500/10 border-amber-500/30 hover:bg-amber-500/20 cursor-pointer' 
                            : 'bg-white/5 border-white/10 opacity-50 cursor-not-allowed'
                        }`}
                      >
                        <span className={`text-[8px] font-black uppercase ${seller.type === 'thrift' ? 'text-amber-400' : seller.type === 'designer' ? 'text-orange-400' : 'text-blue-400'}`}>
                          {seller.type === 'thrift' ? '🕰️' : seller.type === 'designer' ? '✨' : '🏷️'}
                        </span>
                        <span className="text-[9px] text-white/60 font-bold truncate max-w-[80px]">{seller.name}</span>
                        {seller.type === 'thrift' && (
                          <MessageCircle size={10} className="text-amber-400/60" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Order Summary */}
              <div className="mt-8 p-6 rounded-2xl bg-zinc-900 border border-zinc-800">
                <h4 className="text-white font-black uppercase tracking-widest text-xs mb-4">Order Summary</h4>
                <div className="space-y-3 font-mono text-sm">
                  <div className="flex justify-between"><span className="text-zinc-500">Subtotal</span><span className="text-white">{formatCurrency(subtotal)}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-500">Platform Fee</span><span className="text-white">{formatCurrency(platformFee)}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-500">Shipping</span><span className="text-zinc-400 text-xs">Calculated at Checkout</span></div>
                  <div className="border-t border-zinc-800 pt-4 mt-4 flex justify-between items-end">
                    <span className="text-white font-black uppercase tracking-widest text-xs">Total Due</span>
                    <span className="text-2xl font-black text-white italic tracking-tighter">{formatCurrency(total)}</span>
                  </div>
                </div>
              </div>

              {/* 🟢 CHAT NEGOTIATION BUTTON - ONLY FOR THRIFT ITEMS */}
              {sellerType === 'thrift' && (
                <div className="mt-2 p-4 rounded-2xl bg-amber-500/5 border border-amber-500/20">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-500/20 rounded-full">
                      <MessageCircle size={16} className="text-amber-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-[10px] font-black text-amber-400 uppercase tracking-widest">Negotiate with Seller</p>
                      <p className="text-[8px] text-amber-400/60 font-mono">Chat directly to discuss price, condition, or bundle deals</p>
                    </div>
                    <button
                      onClick={() => {
                        const seller = uniqueSellers[0];
                        if (seller) handleOpenChat(seller.id, seller.name);
                      }}
                      className="px-4 py-2 bg-amber-500 text-black font-black text-[9px] uppercase tracking-widest rounded-xl hover:bg-amber-400 transition-colors"
                    >
                      Chat Now
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* FOOTER ACTIONS */}
        {cart.length > 0 && (
          <div className="p-6 bg-black border-t border-zinc-800 flex flex-col gap-3">
            <button onClick={handleCheckout} className="w-full py-5 rounded-xl bg-white text-black font-black text-xs uppercase tracking-[0.2em] hover:bg-zinc-200 transition-transform active:scale-[0.98] flex items-center justify-center gap-2">
              Checkout Securely
            </button>
            
            {/* 🟢 Quick Chat button - ONLY FOR THRIFT ITEMS */}
            {sellerType === 'thrift' && uniqueSellers.length === 1 && (
              <button
                onClick={() => {
                  const seller = uniqueSellers[0];
                  if (seller) handleOpenChat(seller.id, seller.name);
                }}
                className="w-full py-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400 font-black text-[9px] uppercase tracking-widest hover:bg-amber-500/20 transition-colors flex items-center justify-center gap-2"
              >
                <MessageCircle size={14} />
                Message Seller
              </button>
            )}
          </div>
        )}
      </div>

      {/* =========================================================================
          🖥️ DESKTOP LAYOUT (>= 1024px) - Full Screen Split Layout
      ========================================================================= */}
      <div className={`hidden lg:flex absolute inset-0 w-full h-full bg-black overflow-hidden transition-transform duration-500 ease-out ${isOpen ? 'scale-100' : 'scale-[0.98]'}`}>
        
        {/* Floating Close Button */}
        <button onClick={onClose} className="absolute top-8 left-8 z-50 p-4 bg-zinc-900 hover:bg-white hover:text-black text-white rounded-full transition-colors border border-white/10 shadow-2xl">
          <X size={24} />
        </button>

        {/* LEFT PANEL: CART ITEMS (65%) */}
        <div className="w-[65%] h-full overflow-y-auto p-16 pt-24 bg-black border-r border-zinc-800 scrollbar-hide">
            <div className="max-w-4xl mx-auto">
                <div className="flex items-center gap-3 mb-12">
                  <SellerIcon size={32} className={config.color} />
                  <h2 className="text-6xl font-black uppercase italic tracking-tighter text-white">My Bag</h2>
                  {sellerType !== 'unknown' && (
                    <span className={`text-[10px] font-black uppercase px-3 py-1 rounded-full ${config.bg} ${config.color} border ${config.border}`}>
                      {config.label}
                    </span>
                  )}
                </div>

                {cart.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-32 text-zinc-600 opacity-60">
                        <ShoppingBag size={80} strokeWidth={1} className="mb-8" />
                        <p className="font-black uppercase tracking-[0.3em] text-xl text-white mb-4">Bag is empty</p>
                        <p className="text-sm font-mono tracking-widest text-center">Add some fresh items to get started</p>
                        <button onClick={onClose} className="mt-12 py-4 px-8 border border-zinc-700 text-white font-black uppercase tracking-widest text-xs hover:border-white transition-colors">
                            Continue Shopping
                        </button>
                    </div>
                ) : (
                    <div className="space-y-6">
                        <div className="flex justify-between text-zinc-500 text-[10px] font-black uppercase tracking-[0.2em] border-b border-zinc-800 pb-4 mb-6">
                            <span>Product</span>
                            <div className="flex gap-24 pr-4">
                                <span>Quantity</span>
                                <span>Total</span>
                            </div>
                        </div>

                        {cart.map((item) => {
                            const maxStock = item.stock !== undefined ? item.stock : 99;
                            const itemSellerType = getSellerType?.(item.sellerId || item.businessId || '') || 'unknown';
                            const itemQuantity = item.quantity || 1;

                            return (
                              <div key={item.cartId} className="relative flex gap-8 p-6 border border-zinc-800 rounded-2xl bg-zinc-950/50 hover:border-zinc-700 transition-colors group pr-16">
                                  
                                  {/* Desktop Remove Button */}
                                  <button 
                                      type="button" 
                                      onClick={() => remove(item.cartId)} 
                                      className="absolute top-4 right-4 p-3 text-zinc-600 hover:text-red-500 hover:bg-red-500/10 bg-black border border-zinc-800 rounded-full transition-colors z-10"
                                      title="Remove Item"
                                  >
                                      <Trash2 size={16} />
                                  </button>

                                  {/* Image */}
                                  <div className="w-32 h-40 shrink-0 bg-zinc-900 rounded-xl overflow-hidden relative">
                                      <img src={item.image} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" alt={item.name} />
                                      {itemSellerType !== 'unknown' && (
                                        <div className={`absolute top-2 left-2 px-1.5 py-0.5 rounded text-[7px] font-black uppercase ${itemSellerType === 'thrift' ? 'bg-amber-500 text-black' : itemSellerType === 'designer' ? 'bg-orange-500 text-white' : 'bg-blue-500 text-white'}`}>
                                          {itemSellerType === 'thrift' ? 'VTG' : itemSellerType === 'designer' ? 'DSG' : 'BRD'}
                                        </div>
                                      )}
                                  </div>

                                  {/* Content */}
                                  <div className="flex-1 flex items-center justify-between">
                                      
                                      {/* Info */}
                                      <div className="flex flex-col justify-center h-full max-w-[280px]">
                                          <p className="text-[10px] text-zinc-500 font-black uppercase tracking-[0.2em] mb-2">{item.brand}</p>
                                          <h4 className="font-black text-xl text-white uppercase tracking-tight leading-tight mb-2 pr-4">{item.name}</h4>
                                          <p className="text-xs font-mono text-zinc-400">Size: {item.selectedSize} <span className="mx-2">•</span> Color: {item.selectedColor}</p>
                                      </div>

                                      {/* Controls & Price */}
                                      <div className="flex items-center gap-16">
                                          {/* Quantity Selector */}
                                          <div className="flex items-center gap-6 bg-black border border-zinc-800 rounded-xl px-4 py-2">
                                              <button onClick={() => updateQuantity(item.cartId, -1)} disabled={itemQuantity <= 1} className="disabled:opacity-30 text-white hover:text-zinc-400 transition-colors p-2">
                                                  <Minus size={16} />
                                              </button>
                                              <span className="text-lg font-black font-mono w-6 text-center text-white">{itemQuantity}</span>
                                              
                                              <button onClick={() => updateQuantity(item.cartId, 1)} disabled={itemQuantity >= maxStock} className="disabled:opacity-30 text-white hover:text-zinc-400 transition-colors p-2">
                                                  <Plus size={16} />
                                              </button>
                                          </div>

                                          {/* Price */}
                                          <div className="w-32 text-right">
                                              <span className="font-black text-xl tracking-wider text-white">{formatCurrency(item.price * itemQuantity)}</span>
                                          </div>
                                      </div>
                                  </div>
                              </div>
                            )
                        })}
                    </div>
                )}
            </div>
        </div>

        {/* RIGHT PANEL: ORDER SUMMARY (35%) */}
        <div className="w-[35%] h-full bg-zinc-950 p-16 pt-24 flex flex-col justify-between">
            <div>
                <h3 className="text-2xl font-black uppercase tracking-widest text-white mb-10">Order Summary</h3>
                
                {cart.length > 0 ? (
                  <div className="space-y-6 font-mono text-sm">
                      <div className="flex justify-between items-center pb-6 border-b border-zinc-800">
                          <span className="text-zinc-500 uppercase tracking-widest">Subtotal ({cart.length} items)</span>
                          <span className="text-white font-bold text-lg">{formatCurrency(subtotal)}</span>
                      </div>
                      <div className="flex justify-between items-center pb-6 border-b border-zinc-800">
                          <span className="text-zinc-500 uppercase tracking-widest">Platform Fee</span>
                          <span className="text-white font-bold text-lg">{formatCurrency(platformFee)}</span>
                      </div>
                      <div className="flex justify-between items-center pb-6 border-b border-zinc-800">
                          <span className="text-zinc-500 uppercase tracking-widest">Shipping</span>
                          <span className="text-zinc-400 font-bold text-sm">Calculated at Checkout</span>
                      </div>
                      <div className="pt-6 flex justify-between items-end">
                          <span className="text-white font-black uppercase tracking-widest text-sm">Total Due</span>
                          <span className="text-5xl font-black text-white italic tracking-tighter">{formatCurrency(total)}</span>
                      </div>

                      {/* 🟢 Seller info in summary - ONLY SHOW MESSAGE BUTTON FOR THRIFT */}
                      {uniqueSellers.length === 1 && (
                        <div className="mt-6 p-4 rounded-xl bg-zinc-900 border border-zinc-800">
                          <p className="text-[8px] text-white/30 font-black uppercase tracking-widest mb-2">Seller</p>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-zinc-800 border border-white/10 flex items-center justify-center">
                                <Store size={14} className="text-white/30" />
                              </div>
                              <span className="text-white font-bold text-sm">{uniqueSellers[0].name}</span>
                            </div>
                            {/* 🟢 ONLY SHOW CHAT BUTTON FOR THRIFT */}
                            {uniqueSellers[0].type === 'thrift' && (
                              <button
                                onClick={() => handleOpenChat(uniqueSellers[0].id, uniqueSellers[0].name)}
                                className="p-2 text-amber-400/60 hover:text-amber-400 hover:bg-amber-500/10 rounded-lg transition-colors"
                              >
                                <MessageCircle size={16} />
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                  </div>
                ) : (
                  <div className="space-y-6 font-mono text-sm">
                      <div className="pt-6 flex justify-between items-end border-t border-zinc-800 opacity-50">
                          <span className="text-zinc-500 font-black uppercase tracking-widest text-sm">Total Due</span>
                          <span className="text-5xl font-black text-zinc-500 italic tracking-tighter">{formatCurrency(0)}</span>
                      </div>
                  </div>
                )}
            </div>

            <div className="space-y-3">
              {/* 🟢 Negotiation button - ONLY FOR THRIFT */}
              {sellerType === 'thrift' && uniqueSellers.length === 1 && (
                <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[9px] font-black text-amber-400 uppercase tracking-widest">💬 Negotiate</p>
                      <p className="text-[7px] text-amber-400/60 font-mono">Chat with seller about this item</p>
                    </div>
                    <button
                      onClick={() => {
                        const seller = uniqueSellers[0];
                        if (seller) handleOpenChat(seller.id, seller.name);
                      }}
                      className="px-4 py-2 bg-amber-500 text-black font-black text-[8px] uppercase tracking-widest rounded-lg hover:bg-amber-400 transition-colors"
                    >
                      Chat Now
                    </button>
                  </div>
                </div>
              )}

              <button 
                onClick={handleCheckout}
                disabled={cart.length === 0}
                className="w-full py-6 bg-white text-black font-black text-sm uppercase tracking-[0.2em] hover:bg-zinc-200 transition-transform active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-4"
              >
                Checkout Securely <ArrowRight size={18}/>
              </button>

              {/* 🟢 Chat button - ONLY FOR THRIFT */}
              {sellerType === 'thrift' && uniqueSellers.length === 1 && cart.length > 0 && (
                <button
                  onClick={() => {
                    const seller = uniqueSellers[0];
                    if (seller) handleOpenChat(seller.id, seller.name);
                  }}
                  className="w-full py-3 bg-amber-500/10 border border-amber-500/20 text-amber-400 font-black text-[10px] uppercase tracking-widest hover:bg-amber-500/20 rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  <MessageCircle size={14} />
                  Message Seller
                </button>
              )}
            </div>
        </div>

      </div>
    </div>
  );
};

export default CartOverlay;